<template>
  <div class="owner-filter-picker" ref="rootRef" @click.stop>
    <button type="button" class="viewer-toggle" @click="toggleDropdown">
      <div class="viewer-toggle__icon-circle">
        <Icon icon="majesticons:users-line" class="viewer-toggle__icon" />
      </div>
      <div class="viewer-toggle__text">
        <span class="viewer-toggle__label">Viewing:</span>
        <span class="viewer-toggle__value">{{ currentOwnerLabel }}</span>
      </div>
      <Icon
        :icon="isDropdownOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'"
        class="viewer-toggle__chevron"
      />
    </button>

    <div v-if="isDropdownOpen" class="viewer-dropdown">
      <div class="viewer-dropdown__header">
        <Icon icon="uil:user" class="viewer-dropdown__header-icon" />
        <span class="viewer-dropdown__header-text">Select user to view their data</span>
      </div>

      <div class="viewer-dropdown__search">
        <Icon icon="mdi:magnify" class="viewer-dropdown__search-icon" />
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          class="viewer-dropdown__search-input"
          placeholder="Search users..."
          @click.stop
        />
      </div>

      <button
        v-if="showAllOption && filteredUsers.length === users.length"
        type="button"
        class="viewer-option"
        :class="{ 'viewer-option--selected': selectedOwnerKey === 'all' }"
        @click="handleSelectAll"
      >
        <div class="viewer-option__avatar viewer-option__avatar--all">
          <Icon icon="majesticons:users-line" class="viewer-option__avatar-icon" />
        </div>
        <div class="viewer-option__text">
          <span class="viewer-option__name">All Users</span>
          <span class="viewer-option__subtitle">View combined data</span>
        </div>
        <Icon
          v-if="selectedOwnerKey === 'all'"
          icon="mdi:check"
          class="viewer-option__check-icon"
        />
      </button>

      <button
        v-for="user in filteredUsers"
        :key="user.id"
        type="button"
        class="viewer-option"
        :class="{ 'viewer-option--selected': selectedOwnerKey === user.id }"
        @click="handleSelectUser(user)"
      >
        <div class="viewer-option__avatar">
          {{ getInitials(user.fullName) }}
        </div>
        <div class="viewer-option__text">
          <span class="viewer-option__name">{{ user.fullName }}</span>
          <span class="viewer-option__subtitle">{{ getRoleLabel(user.role) }}</span>
        </div>
        <Icon
          v-if="selectedOwnerKey === user.id"
          icon="mdi:check"
          class="viewer-option__check-icon"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { Icon } from "@/components/ui";
import { getRoleDisplayName } from "@/utils/enums";

const props = withDefaults(
  defineProps<{
    modelValue: "all" | string | null;
    users?: Array<{ id: string; fullName?: string; role?: string }>;
    showAllOption?: boolean;
  }>(),
  {
    modelValue: "all",
    users: () => [],
    showAllOption: true,
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: "all" | string];
  select: [owner: "all" | { id: string; fullName?: string; role?: string }];
}>();

const rootRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);
const isDropdownOpen = ref(false);
const searchQuery = ref("");

const filteredUsers = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return props.users;
  return props.users.filter((u) => u.fullName?.toLowerCase().includes(q));
});

const selectedOwnerKey = computed(() => {
  if (props.modelValue === "all" || props.modelValue == null || props.modelValue === "")
    return "all";
  return props.modelValue;
});

const currentOwnerLabel = computed(() => {
  if (selectedOwnerKey.value === "all") return "All Users";
  const user = props.users?.find((u) => u.id === selectedOwnerKey.value);
  return user?.fullName || "All Users";
});

const handleSelectAll = () => {
  isDropdownOpen.value = false;
  searchQuery.value = "";
  emit("update:modelValue", "all");
  emit("select", "all");
};

const handleSelectUser = (user: { id: string; fullName?: string; role?: string }) => {
  isDropdownOpen.value = false;
  searchQuery.value = "";
  emit("update:modelValue", user.id);
  emit("select", user);
};

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
  if (!isDropdownOpen.value) searchQuery.value = "";
};

watch(isDropdownOpen, (open) => {
  if (open) {
    nextTick(() => searchInputRef.value?.focus());
  }
});

const handleClickOutside = (event: MouseEvent) => {
  if (!isDropdownOpen.value) return;
  if (rootRef.value && !rootRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
    searchQuery.value = "";
  }
};

const getInitials = (fullName?: string) => {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/);
  const initials = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "");
  return initials.join("");
};

const getRoleLabel = (role?: string) => {
  if (!role) return "";
  return getRoleDisplayName(role);
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.owner-filter-picker {
  position: relative;
  display: inline-block;
}

.viewer-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  min-width: 0;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.viewer-toggle:hover {
  border-color: #d4d4ff;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
}

.viewer-toggle__icon-circle {
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background-color: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-toggle__icon {
  font-size: 12px;
  color: #4f46e5;
}

.viewer-toggle__text {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.viewer-toggle__label {
  font-size: 0.6875rem;
  color: #9ca3af;
  line-height: 1.3;
}

.viewer-toggle__value {
  font-size: 0.75rem;
  font-weight: 500;
  color: #111827;
  line-height: 1.3;
}

.viewer-toggle__chevron {
  font-size: 12px;
  color: #9ca3af;
  margin-left: 2px;
}

.viewer-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  width: 260px;
  max-width: 260px;
  box-sizing: border-box;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.18),
    0 4px 8px rgba(15, 23, 42, 0.08);
  border: 1px solid #e5e7eb;
  padding: 6px 8px;
  z-index: 9999;
}

.viewer-dropdown__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 6px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.viewer-dropdown__header-icon {
  font-size: 16px;
  color: #4f46e5;
}

.viewer-dropdown__header-text {
  font-size: 0.75rem;
  color: #6b7280;
}

.viewer-dropdown__search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 8px 4px;
  padding: 6px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: border-color 0.15s ease;
}

.viewer-dropdown__search:focus-within {
  border-color: #4f46e5;
  background: #fff;
}

.viewer-dropdown__search-icon {
  font-size: 14px;
  color: #9ca3af;
  flex-shrink: 0;
}

.viewer-dropdown__search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.75rem;
  color: #111827;
  outline: none;
}

.viewer-dropdown__search-input::placeholder {
  color: #9ca3af;
}

.viewer-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  margin: 2px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-radius: 10px;
}

.viewer-option:hover {
  background-color: #f9fafb;
}

.viewer-option--selected {
  background-color: #f5f3ff;
}

.viewer-option__avatar {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background-color: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #4f46e5;
  flex-shrink: 0;
}

.viewer-option__avatar--all {
  background-color: #eef2ff;
}

.viewer-option__avatar-icon {
  font-size: 16px;
  color: #4f46e5;
}

.viewer-option__text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.viewer-option__name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #111827;
}

.viewer-option__subtitle {
  font-size: 0.7rem;
  color: #6b7280;
}

.viewer-option__check-icon {
  font-size: 16px;
  color: #4f46e5;
  flex-shrink: 0;
}
</style>
